import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { getDefaultColumnFilters } from '@/components/table/useTableState';
import {
  combinedFilter,
  createEnumSortingFn,
  createGlobalFilterWithFields,
} from '@/utils/filters.utils';

interface UseTableProps<TData, TValue> {
  data: TData[] | undefined;
  columns: ColumnDef<TData, TValue>[];
  search?: string;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  initialSorting?: SortingState;
  rowSelection?: RowSelectionData;
  getRowId?: (row: TData) => string;
  defaultColumnSize?: number;
  extraSearchableFields?: (row: TData) => Array<string | null | undefined>;
  /** The server applies the filters: the table keeps the state but never filters its rows. */
  manualFiltering?: boolean;
}

export interface RowSelectionData {
  value: RowSelectionState;
  onChange: OnChangeFn<RowSelectionState>;
}

export function useTable<TData, TValue>(props: UseTableProps<TData, TValue>) {
  const {
    data,
    columns,
    search,
    columnFilters: controlledFilters,
    onColumnFiltersChange,
    sorting: controlledSorting,
    onSortingChange,
    initialSorting,
    rowSelection,
    getRowId,
    defaultColumnSize,
    extraSearchableFields,
    manualFiltering = false,
  } = props;

  // Column defaults (meta.defaultFilter) apply at mount; a controlled table receives
  // them through its state controller instead (useTableState).
  const [internalFilters, setInternalFilters] = useState<ColumnFiltersState>(() =>
    getDefaultColumnFilters(columns as ColumnDef<never, never>[])
  );
  const [internalSorting, setInternalSorting] = useState<SortingState>(initialSorting ?? []);

  const isFiltersControlled = controlledFilters !== undefined;
  const isSortingControlled = controlledSorting !== undefined;

  const columnFilters = isFiltersControlled ? controlledFilters : internalFilters;
  const sorting = isSortingControlled ? controlledSorting : internalSorting;

  const stableData = useMemo(() => data ?? [], [data]);

  const columnsWithEnumSorting = useMemo(
    () =>
      columns.map((col): ColumnDef<TData, TValue> => {
        const filterType = col.meta?.filterType;
        if (filterType === 'enum' || filterType === 'badge-enum') {
          return { ...col, sortingFn: createEnumSortingFn(col.meta?.options) };
        }
        return col;
      }),
    [columns]
  );

  return useReactTable({
    data: stableData,
    columns: columnsWithEnumSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter: search,
      columnFilters,
      sorting,
      // Default to an empty selection: passing `undefined` makes TanStack treat rowSelection as
      // controlled-with-undefined, so `getIsAllRowsSelected()` runs `Object.keys(undefined)` and
      // crashes as soon as a select column renders over non-empty data.
      rowSelection: rowSelection?.value ?? {},
    },
    globalFilterFn: createGlobalFilterWithFields(extraSearchableFields),
    manualFiltering,
    // Pagination is handled server-side (infinite query). Without this, every data append makes
    // react-table fire onStateChange to reset its unused pageIndex, adding churn to the render loop.
    autoResetPageIndex: false,
    onColumnFiltersChange: isFiltersControlled ? onColumnFiltersChange : setInternalFilters,
    onSortingChange: isSortingControlled ? onSortingChange : setInternalSorting,
    onRowSelectionChange: rowSelection?.onChange,
    getRowId,
    defaultColumn: {
      size: defaultColumnSize,
      filterFn: combinedFilter,
    },
  });
}
